import {
  Award,
  BadgeCheck,
  Banknote,
  Briefcase,
  Building,
  Building2,
  CalendarCheck,
  CalendarDays,
  CalendarRange,
  CircleDot,
  ClipboardList,
  Coins,
  Download,
  FileBarChart,
  FolderKanban,
  KeySquare,
  LayoutDashboard,
  ListOrdered,
  LogOut,
  PlugZap,
  Receipt,
  Scale,
  ScrollText,
  ShieldCheck,
  SlidersHorizontal,
  Tags,
  UserCog,
  UserRound,
  Users,
  UsersRound,
  WalletCards,
  Workflow,
} from 'lucide-react';
import type { AppConfig } from '@shared/app/app-config';
import type { NavChildItem } from '@shared/components/layout/nav-config';
import {
  PERMISSIONS,
  ROLES_MANAGEMENT_PERMISSIONS,
  crudPermissions,
} from '@shared/constants/permissions';
import { STORAGE_KEYS } from '@shared/constants/storage-keys';
import { PATHS } from '@/router/paths';

/**
 * HRMS navigation.
 *
 * Every destination is gated on the key its route guard and its backend
 * endpoint require — never a role name — so a link is offered exactly when the
 * page will open. `notScopes: ['own']` keeps the administrative, cross-employee
 * screens off the sidebar of someone whose grant reaches only themselves (they
 * have the Employee Portal); the backend still authorizes every request.
 *
 * Not here, by design: companies, clients, invoicing, finance and access
 * administration belong to the Finance application; HRMS reads company master
 * data and shows roles read-only.
 */

const organizationChildren: NavChildItem[] = [
  {
    label: 'Departments',
    to: '/organization/departments',
    icon: Building,
    permission: crudPermissions('departments').view,
  },
  {
    label: 'Teams',
    to: '/organization/teams',
    icon: UsersRound,
    permission: crudPermissions('teams').view,
  },
  {
    label: 'Designations',
    to: '/organization/designations',
    icon: BadgeCheck,
    permission: crudPermissions('designations').view,
  },
  {
    label: 'Skills',
    to: '/organization/skills',
    icon: Award,
    permission: crudPermissions('skills').view,
  },
  {
    label: 'Employment Types',
    to: '/organization/employment-types',
    icon: Briefcase,
    permission: crudPermissions('employment-types').view,
  },
  {
    label: 'Salary Types',
    to: '/organization/salary-types',
    icon: WalletCards,
    permission: crudPermissions('salary-types').view,
  },
  {
    label: 'Employee Status',
    to: '/organization/employee-status',
    icon: CircleDot,
    permission: crudPermissions('employee-statuses').view,
  },
];

const leaveChildren: NavChildItem[] = [
  {
    label: 'Requests',
    to: PATHS.app.leaveRequests,
    icon: ClipboardList,
    permission: PERMISSIONS.leaveRequests.view,
    notScopes: ['own'],
  },
  {
    label: 'Balances',
    to: PATHS.app.leaveBalances,
    icon: Scale,
    permission: PERMISSIONS.leaveBalances.view,
    notScopes: ['own'],
  },
  {
    label: 'Policies',
    to: PATHS.app.leavePolicies,
    icon: SlidersHorizontal,
    permission: PERMISSIONS.leavePolicies.view,
  },
  {
    label: 'Leave Types',
    to: PATHS.app.leaveTypes,
    icon: Tags,
    permission: PERMISSIONS.leaveTypes.view,
  },
];

/**
 * Payroll. Compensation structures and adjustments check `compensation.view`;
 * runs, payment records and transactions check `payroll.view` — separate
 * catalogue keys, so administering employees and seeing pay stay distinct.
 */
const payrollChildren: NavChildItem[] = [
  {
    label: 'Overview',
    to: PATHS.app.payroll,
    icon: LayoutDashboard,
    permission: PERMISSIONS.payroll.view,
    notScopes: ['own'],
    end: true,
  },
  {
    label: 'Employees',
    to: PATHS.app.payrollRuns,
    icon: Users,
    permission: PERMISSIONS.payroll.view,
    notScopes: ['own'],
  },
  {
    label: 'Compensation',
    to: PATHS.app.payrollCompensation,
    icon: Coins,
    permission: PERMISSIONS.compensation.view,
    notScopes: ['own'],
    activePrefixes: [PATHS.app.payrollAdjustments],
  },
  {
    label: 'Payments',
    to: PATHS.app.payrollPayments,
    icon: Receipt,
    permission: PERMISSIONS.payroll.view,
    notScopes: ['own'],
  },
];

const projectChildren: NavChildItem[] = [
  {
    label: 'Projects',
    to: PATHS.app.projects,
    icon: FolderKanban,
    permission: PERMISSIONS.projects.view,
    end: true,
  },
  {
    label: 'Project Statuses',
    to: PATHS.app.projectStatuses,
    icon: CircleDot,
    permission: crudPermissions('project-statuses').view,
    heading: 'Setup',
  },
  {
    label: 'Module Statuses',
    to: PATHS.app.moduleStatuses,
    icon: ListOrdered,
    permission: crudPermissions('module-statuses').view,
  },
  {
    label: 'Project Roles',
    to: PATHS.app.projectRoles,
    icon: UserCog,
    permission: crudPermissions('project-roles').view,
  },
];

const integrationChildren: NavChildItem[] = [
  {
    label: 'TeamLogger',
    to: PATHS.app.teamLogger,
    icon: PlugZap,
    permission: PERMISSIONS.teamlogger.view,
    end: true,
  },
  {
    label: 'Project Mapping',
    to: PATHS.app.projectMappings,
    icon: Workflow,
    permission: PERMISSIONS.projectMappings.view,
    activePrefixes: [PATHS.app.teamLoggerManualReview],
  },
  {
    label: 'Asana',
    to: PATHS.app.asana,
    icon: Workflow,
    permission: PERMISSIONS.asana.view,
  },
];

const reportChildren: NavChildItem[] = [
  { label: 'User Logs', to: PATHS.app.userLogs, icon: Users, permission: PERMISSIONS.reports.view },
  {
    label: 'Project Logs',
    to: PATHS.app.projectLogs,
    icon: FolderKanban,
    permission: PERMISSIONS.reports.view,
  },
  { label: 'Exports', to: PATHS.app.exports, icon: Download, permission: PERMISSIONS.export.generate },
];

export const hrmsConfig: AppConfig = {
  application: 'HRMS',
  name: 'HRMS',
  tagline: 'People, time, leave, payroll and projects.',
  usesPermissions: true,
  fullHeightRoutes: [PATHS.app.teamLogger],
  navigation: [
    {
      items: [
        {
          type: 'link',
          label: 'Dashboard',
          to: PATHS.app.dashboard,
          icon: LayoutDashboard,
          permission: PERMISSIONS.dashboard.view,
        },
      ],
    },
    {
      heading: 'People',
      items: [
        {
          // A self-service grant reaches one record: offer it, not a directory.
          type: 'link',
          label: 'My Profile',
          to: PATHS.app.myProfile,
          icon: UserRound,
          permission: PERMISSIONS.employees.view,
          scopes: ['own'],
        },
        {
          type: 'link',
          label: 'Employees',
          to: PATHS.app.employees,
          icon: Users,
          permission: PERMISSIONS.employees.view,
          notScopes: ['own'],
        },
        {
          type: 'link',
          label: 'Users',
          to: PATHS.app.users,
          icon: UserCog,
          permission: PERMISSIONS.users.view,
        },
        {
          type: 'group',
          label: 'Organization',
          icon: Building2,
          storageKey: STORAGE_KEYS.SIDEBAR,
          matchPrefix: '/organization',
          children: organizationChildren,
        },
      ],
    },
    {
      heading: 'Time & leave',
      items: [
        {
          type: 'link',
          label: 'Attendance',
          to: PATHS.app.attendance,
          icon: CalendarCheck,
          permission: PERMISSIONS.attendance.view,
          notScopes: ['own'],
        },
        {
          type: 'group',
          label: 'Leave',
          icon: CalendarRange,
          storageKey: STORAGE_KEYS.LEAVE_SIDEBAR,
          matchPrefix: '/leave',
          children: leaveChildren,
        },
        {
          type: 'link',
          label: 'Holidays',
          to: PATHS.app.holidays,
          icon: CalendarDays,
          permission: PERMISSIONS.holidays.view,
        },
        {
          type: 'link',
          label: 'Separations',
          to: PATHS.app.separations,
          icon: LogOut,
          permission: PERMISSIONS.employeeExits.view,
          notScopes: ['own'],
        },
      ],
    },
    {
      heading: 'Pay',
      items: [
        {
          type: 'group',
          label: 'Payroll',
          icon: Banknote,
          storageKey: STORAGE_KEYS.PAYROLL_SIDEBAR,
          matchPrefix: PATHS.app.payroll,
          children: payrollChildren,
        },
      ],
    },
    {
      heading: 'Work',
      items: [
        {
          type: 'group',
          label: 'Projects',
          icon: FolderKanban,
          storageKey: STORAGE_KEYS.PROJECTS_SIDEBAR,
          matchPrefix: PATHS.app.projects,
          children: projectChildren,
        },
        {
          type: 'group',
          label: 'Integrations',
          icon: PlugZap,
          storageKey: STORAGE_KEYS.INTEGRATIONS_SIDEBAR,
          matchPrefix: '/integrations',
          children: integrationChildren,
        },
        {
          type: 'group',
          label: 'Reports',
          icon: FileBarChart,
          storageKey: STORAGE_KEYS.REPORTS_SIDEBAR,
          matchPrefix: '/reports',
          children: reportChildren,
        },
      ],
    },
    {
      heading: 'Access',
      items: [
        {
          // Read-only in HRMS (role writes are Finance's). `roles.view` alone is
          // held by everyone for role pickers, so the screen is offered to
          // those who manage or assign roles.
          type: 'link',
          label: 'Roles',
          to: PATHS.app.roles,
          icon: ShieldCheck,
          anyOf: [...ROLES_MANAGEMENT_PERMISSIONS, PERMISSIONS.roles.assign],
        },
        {
          type: 'link',
          label: 'Permissions',
          to: PATHS.app.permissions,
          icon: KeySquare,
          permission: PERMISSIONS.permissions.view,
        },
        {
          type: 'link',
          label: 'Audit Log',
          to: PATHS.app.auditLogs,
          icon: ScrollText,
          permission: PERMISSIONS.auditLogs.view,
        },
      ],
    },
  ],
  userMenu: [
    {
      label: 'My Profile',
      to: PATHS.app.myProfile,
      icon: UserRound,
      permission: PERMISSIONS.employees.view,
    },
  ],
};
